import {Component, ViewChild} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {firstValueFrom} from 'rxjs';
import {
  ApiGatewayService, GatewayState,
  RegisteredGateway,
  StatMessage
} from 'src/app/services/api-gateway.service';
import {delay, emitInfoMessage, InfoMessageService} from 'src/app/services/info-message.service';
import {KeyPairDialogComponent} from "../../key-pair-dialog/key-pair-dialog.component";
import {GatewayDialogComponent} from "../../gateway-dialog/gateway-dialog.component";
import {DeleteGatewayDialogComponent} from "../../delete-gateway-dialog/delete-gateway-dialog.component";
import { ConfirmKeyPairDialogComponent } from 'src/app/confirm-key-pair-dialog/confirm-key-pair-dialog.component';
import {DateTimeUtilsService} from "../../services/date-time-utils.service";


export interface DialogData {
  dialogType: 'update' | 'register'
  deviceData: RegisteredGateway
}

export interface DialogRemoveData {
  deviceData: RegisteredGateway
}

@Component({
    selector: 'tab-gateways',
    templateUrl: './tab-gateways.component.html',
    standalone: false
})
export class TabGatewaysComponent {

  selectedGateway?: RegisteredGateway;
  gateways?: RegisteredGateway[];
  gatewayStats?: StatMessage[];
  maxUserGateways: number = 0;


  constructor(private infoMessageService: InfoMessageService,
              private gatewayApiService: ApiGatewayService,
              public dateTimeUtils: DateTimeUtilsService,
              public dialog: MatDialog) {
  }

  async ngOnInit() {
    // Get Devices
    this.getRegisteredGatewaysUser();
    // this.maxUserGateways = this.keycloakUserService.getUserMaxGateways();
  }

  updateSelectedGatewayStats() {
    if (this.selectedGateway) this.getGatewayStats(this.selectedGateway)
  }


  async getRegisteredGatewaysUser() {
    await this.gatewayApiService.getRegisteredGateways()
      .then((data) => {
        if (data.body) {
          this.gateways = data.body
          // this.registeredGateways = new MatTableDataSource(data.body);
          // this.registeredGateways.sort = this.sortGateways;
          if (data.body.length == 0) {
            emitInfoMessage("No gateways found for user", this.infoMessageService)
          } else {
            this.gateways.forEach((gateway: RegisteredGateway) => {
              this.gatewayApiService.getGatewayState(gateway).then(r => {
                gateway.state = r.body ? r.body : undefined;
              });
            })
          }
        }
      })
      .catch((error) => {
        emitInfoMessage(error.status + ": " + error.statusText, this.infoMessageService)
      })
  }

  async broadCastAlmanacNow(gateway: RegisteredGateway) {
    await this.gatewayApiService.broadcastAlmanac(gateway)
      .then(() => {
        emitInfoMessage("Broadcasting", this.infoMessageService)
        delay(500);
      })
      .catch((error) => {
        emitInfoMessage(error.status + ": " + error.statusText, this.infoMessageService)
      })
  }

  async generateKeyPair(gateway: RegisteredGateway) {
    const dialogRef = this.dialog.open(ConfirmKeyPairDialogComponent, {
      width: '300px',
      data: {}
    });

    const dialogResult = await firstValueFrom(dialogRef.afterClosed());

    if (dialogResult) {
      await this.gatewayApiService.generateKeyPair(gateway).then(r => {
        // Open dialog with existing or new made device as data
        const dialogRef = this.dialog.open(KeyPairDialogComponent, {
          data: r.body
        });
      })
        .catch((error) => {
          emitInfoMessage(error.status + ": " + error.statusText, this.infoMessageService)
        })
    }
  }

  async deleteRegisteredGateway(toDeleteDevice: RegisteredGateway) {
    if (this.selectedGateway === toDeleteDevice) {
      this.selectedGateway = undefined;
    }
    await this.gatewayApiService.deleteRegisteredGateway(toDeleteDevice)
      .then(() => {
        emitInfoMessage("Deleted gateway", this.infoMessageService)
        delay(500);
        this.getRegisteredGatewaysUser();
      })
      .catch((error) => {
        emitInfoMessage(error.status + ": " + error.statusText, this.infoMessageService)
      })
  }

  async confirmDelete(toDeleteDevice: RegisteredGateway) {
    // Open dialog with existing or new made device as data
    const dialogRef = this.dialog.open(DeleteGatewayDialogComponent, {
      data: {deviceData: toDeleteDevice}
    });

    // Wait for first value from dialog and save
    let dialogResult = await firstValueFrom(dialogRef.afterClosed())

    // If dialog was clicked on submit (returning a true bool), i.e. not cancelled
    if (dialogResult) {
      this.deleteRegisteredGateway(toDeleteDevice)
    }
  }


  async getGatewayStats(gateway: RegisteredGateway) {
    this.selectedGateway = gateway;
    await this.gatewayApiService.getGatewayStats(gateway)
      .then((data) => {
        if (data.body) {
          this.gatewayStats = data.body
          // this.selectedStatMessages = new MatTableDataSource(data.body)
          // this.selectedStatMessages.sort = this.sortStats;
          if (data.body.length == 0) {
            emitInfoMessage("No stats found for gateway", this.infoMessageService)
          }
        }
      })
      .catch((error) => {
        emitInfoMessage(error.status + ": " + error.statusText, this.infoMessageService)
      })
  }

  async openDialogGateway(dialogType: 'update' | 'register', toUpdateGateway?: RegisteredGateway) {
    // For register we make a new empty device
    if (!toUpdateGateway) toUpdateGateway = {gateway_id: '', mode: 'idle', power: 'low', country_code: 'NL'}

    // Open dialog with existing or new made device as data
    const dialogRef = this.dialog.open(GatewayDialogComponent, {
      data : {dialogType : dialogType, deviceData : toUpdateGateway}
    });

    // Wait for first value from dialog and save
    let dialogResult = await firstValueFrom(dialogRef.afterClosed())

    // If dialog was clicked on submit (returning a true bool), i.e. not cancelled
    if (dialogResult){
      if (dialogType == 'register'){
        this.addRegisteredGatewayDialog(toUpdateGateway)
      } else if (dialogType == 'update'){
        this.updateRegisteredDeviceDialog(toUpdateGateway)
      } else {
        emitInfoMessage("Cannot handle return values from dialog", this.infoMessageService)
      }
    }
  }

  async addRegisteredGatewayDialog(newGateway: RegisteredGateway) {
    await this.gatewayApiService.addGateway(newGateway)
      .then(() => {
        emitInfoMessage("Added gateway", this.infoMessageService)
        delay(500);
        this.getRegisteredGatewaysUser();
      })
      .catch((error) => {
        emitInfoMessage(error.status + ": " + error.statusText, this.infoMessageService)
      })
  }

  async updateRegisteredDeviceDialog(toUpdateGateway: RegisteredGateway) {
    if (!toUpdateGateway) {
      emitInfoMessage("Error in updating gateway, no gateway found to edit", this.infoMessageService)
      return
    }
    await this.gatewayApiService.updateRegisteredGateway(toUpdateGateway)
      .then(() => {
        emitInfoMessage("Updated device", this.infoMessageService)
        delay(500);
        this.getRegisteredGatewaysUser();
      })
      .catch((error) => {
        emitInfoMessage(error.status + ": " + error.statusText, this.infoMessageService)
      })
  }

  isSelectedGateway(gateway: RegisteredGateway) {
    if(gateway === this.selectedGateway){
      return "bg-[#E6DBF9] dark:bg-[#250B47] dark:text-white selected-gateway"
    }
    return "bg-white dark:bg-[#171717] dark:text-white"
  }

  getStatusClass(state: GatewayState | undefined) {
    if(state?.is_connected){
      return 'bg-[#63E2BE]';
    }
    return 'bg-[#C84143]';
  }
}
